import React, { useEffect, useState } from 'react';
import Modal from 'react-modal';

import {
  BotonNaranja,
  CalendarField,
  CalendarFieldLabel,
  modalStyles,
  mobileModalStyles,
} from '../../Css/StyledCalendarBase.js';

import {
  StyledModalDynamic,
  CalendarModalCard,
  CalendarModalTitle,
  CalendarModalDescription,
  CalendarModalActions,
  CalendarModalCloseButton,
  CalendarFormLayout,
  CalendarSelect,
  CancelButton,
} from '../../Css/StyledCalendarModal.js';

import { CloseIcon } from './CalendarIcons.js';

const HOUR_OPTIONS = [
  '06:00',
  '07:00',
  '08:00',
  '09:00',
  '10:00',
  '11:00',
  '12:00',
  '13:00',
  '14:00',
  '15:00',
  '16:00',
  '17:00',
  '18:00',
  '19:00',
  '20:00',
  '21:00',
  '22:00',
  '23:00'
];

// Estado interno seguro para el modal.
// No representa la configuración real del usuario.
// La configuración real siempre debe venir de Redux/backend.
const createEmptySettings = () => ({
  visibleTimeRanges: [
    {
      start: '',
      end: ''
    }
  ],
  useSecondRange: false,
  secondRange: {
    start: '',
    end: ''
  },
  autoScrollToCurrentTime: true
});

const CalendarSettingsModal = ({
  isOpen,
  onClose,
  onSave,
  currentSettings = null
}) => {
  const [isMobile, setIsMobile] = useState(false);
  const [settings, setSettings] = useState(createEmptySettings());

  useEffect(() => {
    const checkScreenSize = () => {
      setIsMobile(window.innerWidth <= 480);
    };

    checkScreenSize();
    window.addEventListener('resize', checkScreenSize);

    return () => window.removeEventListener('resize', checkScreenSize);
  }, []);

    useEffect(() => {
    if (!isOpen) {
        return;
    }

    // Si todavía no llegó la configuración desde Redux/backend,
    // no inventamos 14:00–20:00 desde el front.
    if (!currentSettings || !Array.isArray(currentSettings.visibleTimeRanges)) {
        setSettings(createEmptySettings());
        return;
    }

    const firstRange = currentSettings.visibleTimeRanges[0] || {
        start: '',
        end: ''
    };

    const secondRange = currentSettings.visibleTimeRanges[1] || {
        start: '',
        end: ''
    };

    setSettings({
        visibleTimeRanges: [
        {
            start: firstRange.start || '',
            end: firstRange.end || ''
        }
        ],
        useSecondRange: Boolean(currentSettings.visibleTimeRanges[1]),
        secondRange: {
        start: secondRange.start || '',
        end: secondRange.end || ''
        },
        autoScrollToCurrentTime:
        currentSettings.autoScrollToCurrentTime !== false
    });
    }, [isOpen, currentSettings]);

  const currentModalStyles = isMobile ? mobileModalStyles : modalStyles;

  const handleFirstRangeChange = (field, value) => {
    setSettings((prev) => ({
      ...prev,
      visibleTimeRanges: [
        {
          ...prev.visibleTimeRanges[0],
          [field]: value
        }
      ]
    }));
  };

  const handleSecondRangeChange = (field, value) => {
    setSettings((prev) => ({
      ...prev,
      secondRange: {
        ...prev.secondRange,
        [field]: value
      }
    }));
  };

  const handleUseSecondRangeChange = (checked) => {
    setSettings((prev) => ({
      ...prev,
      useSecondRange: checked
    }));
  };

  const handleAutoScrollChange = (checked) => {
    setSettings((prev) => ({
      ...prev,
      autoScrollToCurrentTime: checked
    }));
  };

  const handleShowFullDay = async () => {
    const fullDaySettings = {
      visibleTimeRanges: [
        {
          start: '06:00',
          end: '23:00'
        }
      ],
      autoScrollToCurrentTime: false
    };

    const saved = await onSave(fullDaySettings);

    if (saved) {
    onClose();
    }
  };

  const handleSave = async () => {
    const firstRange = settings.visibleTimeRanges[0];

    if (!firstRange.start || !firstRange.end) {
      alert('Completá el horario visible principal.');
      return;
    }

    if (firstRange.start > firstRange.end) {
    alert('En el primer rango, la hora desde no puede ser posterior a la hora hasta.');
    return;
    }

    const visibleTimeRanges = [
      {
        start: firstRange.start,
        end: firstRange.end
      }
    ];

    if (settings.useSecondRange) {
        if (!settings.secondRange.start || !settings.secondRange.end) {
            alert('Completá el segundo rango horario.');
            return;
        }

        if (settings.secondRange.start > settings.secondRange.end) {
            alert('En el segundo rango, la hora desde no puede ser posterior a la hora hasta.');
            return;
        }

        visibleTimeRanges.push({
            start: settings.secondRange.start,
            end: settings.secondRange.end
        });
    }

    const saved = await onSave({
        visibleTimeRanges,
        autoScrollToCurrentTime: settings.autoScrollToCurrentTime
    });

    if (saved) {
        onClose();
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={onClose}
      style={currentModalStyles}
      contentLabel="Configurar vista del calendario"
    >
      <StyledModalDynamic>
        <CalendarModalCard>
          <CalendarModalCloseButton onClick={onClose} aria-label="Cerrar modal">
            <CloseIcon width={20} height={20} />
          </CalendarModalCloseButton>

          <CalendarModalTitle>
            Configurar vista
          </CalendarModalTitle>

          <CalendarModalDescription>
            Estos horarios solo modifican lo que ves en pantalla. No impiden crear turnos fuera de ese rango.
          </CalendarModalDescription>

          <CalendarFormLayout>
            <CalendarField>
              <CalendarFieldLabel htmlFor="calendar-visible-start">
                Mostrar desde
              </CalendarFieldLabel>

              <CalendarSelect
                id="calendar-visible-start"
                value={settings.visibleTimeRanges[0]?.start || ''}
                onChange={(e) => handleFirstRangeChange('start', e.target.value)}
              >
                <option value="">Seleccionar</option>
                {HOUR_OPTIONS.map((hour) => (
                  <option key={`start-${hour}`} value={hour}>
                    {hour}
                  </option>
                ))}
              </CalendarSelect>
            </CalendarField>

            <CalendarField>
              <CalendarFieldLabel htmlFor="calendar-visible-end">
                Mostrar hasta
              </CalendarFieldLabel>

              <CalendarSelect
                id="calendar-visible-end"
                value={settings.visibleTimeRanges[0]?.end || ''}
                onChange={(e) => handleFirstRangeChange('end', e.target.value)}
              >
                <option value="">Seleccionar</option>
                {HOUR_OPTIONS.map((hour) => (
                  <option key={`end-${hour}`} value={hour}>
                    {hour}
                  </option>
                ))}
              </CalendarSelect>
            </CalendarField>

            <CalendarField data-span="full">
              <label style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                <input
                  type="checkbox"
                  checked={settings.useSecondRange}
                  onChange={(e) => handleUseSecondRangeChange(e.target.checked)}
                />
                Usar segundo rango horario
              </label>

              <small style={{ color: '#666' }}>
                Usalo si trabajás en dos bloques, por ejemplo 08:00–12:00 y 16:00–22:00.
              </small>
            </CalendarField>

            {settings.useSecondRange && (
              <>
                <CalendarField>
                  <CalendarFieldLabel htmlFor="calendar-second-visible-start">
                    Segundo rango desde
                  </CalendarFieldLabel>

                  <CalendarSelect
                    id="calendar-second-visible-start"
                    value={settings.secondRange.start || ''}
                    onChange={(e) => handleSecondRangeChange('start', e.target.value)}
                  >
                    <option value="">Seleccionar</option>
                    {HOUR_OPTIONS.map((hour) => (
                      <option key={`second-start-${hour}`} value={hour}>
                        {hour}
                      </option>
                    ))}
                  </CalendarSelect>
                </CalendarField>

                <CalendarField>
                  <CalendarFieldLabel htmlFor="calendar-second-visible-end">
                    Segundo rango hasta
                  </CalendarFieldLabel>

                  <CalendarSelect
                    id="calendar-second-visible-end"
                    value={settings.secondRange.end || ''}
                    onChange={(e) => handleSecondRangeChange('end', e.target.value)}
                  >
                    <option value="">Seleccionar</option>
                    {HOUR_OPTIONS.map((hour) => (
                      <option key={`second-end-${hour}`} value={hour}>
                        {hour}
                      </option>
                    ))}
                  </CalendarSelect>
                </CalendarField>
              </>
            )}

            <CalendarField data-span="full">
              <label style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                <input
                  type="checkbox"
                  checked={settings.autoScrollToCurrentTime}
                  onChange={(e) => handleAutoScrollChange(e.target.checked)}
                />
                Posicionarme automáticamente cerca de la hora actual
              </label>

              <small style={{ color: '#666' }}>
                Si está activado, al entrar el calendario intentará mostrarte cerca del horario actual.
              </small>
            </CalendarField>
          </CalendarFormLayout>

          <CalendarModalActions>
            <BotonNaranja type="button" onClick={handleSave}>
              GUARDAR
            </BotonNaranja>

            <CancelButton type="button" onClick={handleShowFullDay}>
              MOSTRAR TODO EL DÍA
            </CancelButton>

            {isMobile && (
              <CancelButton type="button" onClick={onClose}>
                CANCELAR
              </CancelButton>
            )}
          </CalendarModalActions>
        </CalendarModalCard>
      </StyledModalDynamic>
    </Modal>
  );
};

export default CalendarSettingsModal;